// src/pages/HowItWorks.jsx

import React, { useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FilePlus,
  Users,
  MessageCircle,
  Wand2,
  CheckCircle,
  DollarSign,
  Briefcase,
  TrendingUp,
  Plus,
  Minus,
  Settings,
  Eye,
  Search,
  Filter,
  BarChart3,
  Clock,
  Shield,
} from "lucide-react";

import NavBar from "../components/layout/NavBar";

//================================================================//
//  🎨 DESIGN SYSTEM: TOKENS & THEME (Matching other pages)
//================================================================//

const theme = {
  colors: {
    primary: "#3b82f6",
    primaryLight: "#eff6ff",
    primaryText: "#3b82f6",
    textHeading: "#0f172a",
    textBody: "#334155",
    textMuted: "#64748b",
    background: "#f8fafc",
    surface: "#ffffff",
    border: "#e2e8f0",
    success: "#059669",
    // Step colors - more subtle
    step1: { bg: "#f0f7ff", color: "#2563eb" }, // Light Blue
    step2: { bg: "#ecfdf5", color: "#059669" }, // Light Green
    step3: { bg: "#faf5ff", color: "#a855f7" }, // Light Purple
    step4: { bg: "#fffbf0", color: "#f59e0b" }, // Light Orange
    // Flow step colors - subtle
    flow1: { 
      bg: "#eff6ff",
      color: "#2563eb",
      shadow: "0 2px 8px rgba(59, 130, 246, 0.15)"
    },
    flow2: { 
      bg: "#ecfdf5",
      color: "#059669",
      shadow: "0 2px 8px rgba(34, 197, 94, 0.15)"
    },
    flow3: { 
      bg: "#faf5ff",
      color: "#a855f7",
      shadow: "0 2px 8px rgba(168, 85, 247, 0.15)"
    },
    flow4: { 
      bg: "#fffbf0",
      color: "#f59e0b",
      shadow: "0 2px 8px rgba(249, 115, 22, 0.15)"
    },
    flow5: { 
      bg: "#fdf2f8",
      color: "#ec4899",
      shadow: "0 2px 8px rgba(236, 72, 153, 0.15)"
    },
  },
  shadows: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.06)",
    md: "0 6px 20px rgba(15, 23, 42, 0.07)",
  },
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  radius: "14px",
};

//================================================================//
//  ✨ ANIMATIONS & GLOBAL STYLES
//================================================================//

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideInUp = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${theme.colors.background};
    font-family: ${theme.font};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

//================================================================//
//  📄 PAGE LAYOUT & STRUCTURE
//================================================================//

const PageWrapper = styled.div`
  background: ${theme.colors.background};
  padding-top: 14px; /* space for NavBar */
  min-height: 100vh;
  animation: ${fadeIn} 0.5s ease;
`;

const ContentContainer = styled.div`
  max-width: 1024px;
  margin: 0 auto;
  padding: 2rem;
`;

const Hero = styled.section`
  text-align: center;
  padding: 4rem 1rem 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0;
  letter-spacing: -0.03em;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  max-width: 600px;
  margin: 1rem auto 0;
  font-size: 1.125rem;
  color: ${theme.colors.textMuted};
  line-height: 1.6;
`;

const ContentPane = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.radius};
  box-shadow: ${theme.shadows.md};
  padding: 3rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 4rem;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  animation: ${slideInUp} 0.6s ease-out;
`;

const SectionHeader = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  position: relative;
  padding-bottom: 0.75rem;
  margin: 0;
  border-bottom: 1px solid ${theme.colors.border};
`;

const Step = styled.div`
  display: flex;
  gap: 1rem;
`;

const IconCircle = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.$bgColor || theme.colors.primaryLight};
  color: ${props => props.$color || theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StepTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${theme.colors.textBody};
  margin: 0 0 0.25rem;
`;

const StepText = styled.p`
  font-size: 0.95rem;
  color: ${theme.colors.textMuted};
  margin: 0;
  line-height: 1.6;
`;

//================================================================//
//  🧠 FAQ SECTION — Styled to match the provided screenshot
//================================================================//

/** Wrapper with centered heading + subtitle (no card) */
const FAQSectionWrap = styled.section`
  padding: 0 0 4rem;
`;

const FAQHeading = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 2.5rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.03em;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 2.4rem;
  }
`;

const FAQSubheading = styled.p`
  text-align: center;
  max-width: 760px;
  margin: 0 auto 2rem;
  color: ${theme.colors.textMuted};
  font-size: 1.05rem;
  line-height: 1.7;
`;

/** List that centers wide, pill cards */
const FAQList = styled.div`
  display: grid;
  gap: 20px;
  margin: 0 auto;
`;

const PillCard = styled.div`
  border-radius: 24px;
  background: #f5f7fb; /* light gray like screenshot */
  border: 1px solid #eef2f7; /* subtle border */
  overflow: hidden;
`;

/** Button area with bold question on left and +/- on right */
const FAQButton = styled.button`
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  padding: 22px 28px;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: background 0.15s ease;

  h3 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.35;
    letter-spacing: -0.01em;
  }

  svg {
    width: 24px;
    height: 24px;
    color: #0f172a;
  }
`;

/** Collapsible answer area inside the pill card */
const FAQPanel = styled.div`
  padding: ${(p) => (p.$open ? "0 28px 22px" : "0 28px")};
  max-height: ${(p) => (p.$open ? "500px" : "0px")};
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;

  p {
    margin: 0;
    color: #475569;
    line-height: 1.7;
    font-size: 0.95rem;
    padding-top: ${(p) => (p.$open ? "8px" : "0")};
  }
`;

/** One FAQ Item */
const FAQItem = ({ id, q, a }) => {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((o) => !o);

  return (
    <PillCard>
      <FAQButton
        aria-expanded={open}
        aria-controls={`panel-${id}`}
        onClick={toggle}
      >
        <h3>{q}</h3>
        {open ? <Minus aria-hidden /> : <Plus aria-hidden />}
      </FAQButton>
      <FAQPanel id={`panel-${id}`} $open={open}>
        <p>{a}</p>
      </FAQPanel>
    </PillCard>
  );
};

//================================================================//
//  🚀 PLATFORM FEATURES SECTION
//================================================================//

const FeaturesSectionWrap = styled.section`
  padding: 0 0 4rem;
`;

const FeaturesHeading = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 2.5rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.03em;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 2.4rem;
  }
`;

const FeaturesSubheading = styled.p`
  text-align: center;
  max-width: 760px;
  margin: 0 auto 3rem;
  color: ${theme.colors.textMuted};
  font-size: 1.05rem;
  line-height: 1.7;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const FeatureCard = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.radius};
  box-shadow: ${theme.shadows.sm};
  border: 1px solid ${theme.colors.border};
  padding: 2rem;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
    border-color: ${theme.colors.primary};
  }
`;

const FeatureIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0 0 0.75rem;
`;

const FeatureDescription = styled.p`
  color: ${theme.colors.textMuted};
  line-height: 1.6;
  margin: 0 0 1rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureListItem = styled.li`
  color: ${theme.colors.textBody};
  font-size: 0.95rem;
  padding: 0.25rem 0;
  position: relative;
  padding-left: 1.25rem;

  &:before {
    content: "•";
    color: ${theme.colors.primary};
    font-weight: bold;
    position: absolute;
    left: 0;
  }
`;

const UserFlowSection = styled.div`
  background: linear-gradient(135deg, ${theme.colors.surface} 0%, #fafbff 100%);
  border-radius: 24px;
  border: 1px solid ${theme.colors.primaryLight};
  padding: 4rem 3rem;
  margin-bottom: 3rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${theme.colors.primary} 0%, #6366f1 100%);
  }

  @media (max-width: 768px) {
    padding: 3rem 2rem;
    border-radius: 16px;
  }
`;

const FlowTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 800;
  color: ${theme.colors.textHeading};
  text-align: center;
  margin: 0 0 1rem;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 1.875rem;
  }
`;

const FlowSubtitle = styled.p`
  text-align: center;
  color: ${theme.colors.textMuted};
  margin: 0 0 4rem;
  font-size: 1.125rem;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 4rem;
`;

const FlowSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 2.5rem;
  position: relative;
`;

const FlowStep = styled.div`
  text-align: center;
  position: relative;
  background: ${theme.colors.surface};
  border-radius: 16px;
  padding: 2rem 1.5rem;
  border: 1px solid ${theme.colors.border};
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 32px rgba(15, 23, 42, 0.12);
    border-color: ${theme.colors.primary};
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const StepNumber = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${props => props.$bgColor || theme.colors.primaryLight};
  color: ${props => props.$color || theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1.25rem;
  margin: 0 auto 1.5rem;
  box-shadow: ${props => props.$shadow || '0 2px 8px rgba(59, 130, 246, 0.15)'};
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const FlowStepTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0 0 0.75rem;
  letter-spacing: -0.01em;
`;

const FlowStepText = styled.p`
  color: ${theme.colors.textMuted};
  font-size: 1rem;
  line-height: 1.6;
  margin: 0 0 1.5rem;
`;

const StepButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, #6366f1 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  margin: 0 0.25rem;

  &:hover {
    background: linear-gradient(135deg, #2563eb 0%, #6366f1 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4);
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const PlatformFeaturesSection = ({ user }) => {
  const navigate = useNavigate();

  return (
    <FeaturesSectionWrap>
      <UserFlowSection>
        <FlowTitle>How Doitly Works</FlowTitle>
        <FlowSubtitle>
          From home repairs to creative projects, connect with skilled people ready to help. Simple, fast, and effective.
        </FlowSubtitle>

        <FlowSteps>
          <FlowStep>
            <StepNumber $bgColor={theme.colors.flow1.bg} $color={theme.colors.flow1.color} $shadow={theme.colors.flow1.shadow}>1</StepNumber>
            <FlowStepTitle>Post Your Task</FlowStepTitle>
            <FlowStepText>
              Create your task listing with clear details and budget. The more specific you are, the better offers you'll receive.
            </FlowStepText>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <StepButton onClick={() => navigate("/posttask")}>
                Post a Task
              </StepButton>
              <StepButton onClick={() => navigate("/tasks")}>
                Browse Tasks
              </StepButton>
            </div>
          </FlowStep>

          <FlowStep>
            <StepNumber $bgColor={theme.colors.flow2.bg} $color={theme.colors.flow2.color} $shadow={theme.colors.flow2.shadow}>2</StepNumber>
            <FlowStepTitle>Get Offers</FlowStepTitle>
            <FlowStepText>
              Receive competitive offers from qualified helpers. Each includes pricing, timeline, and their unique approach.
            </FlowStepText>
            {user && (
              <StepButton onClick={() => navigate("/dashboard")}>
                View Dashboard
              </StepButton>
            )}
          </FlowStep>

          <FlowStep>
            <StepNumber $bgColor={theme.colors.flow3.bg} $color={theme.colors.flow3.color} $shadow={theme.colors.flow3.shadow}>3</StepNumber>
            <FlowStepTitle>Pick Someone</FlowStepTitle>
            <FlowStepText>
              Select your preferred helper based on their proposal, experience, reviews, and communication style.
            </FlowStepText>
          </FlowStep>

          <FlowStep>
            <StepNumber $bgColor={theme.colors.flow4.bg} $color={theme.colors.flow4.color} $shadow={theme.colors.flow4.shadow}>4</StepNumber>
            <FlowStepTitle>Chat & Collaborate</FlowStepTitle>
            <FlowStepText>
              Communicate through our platform to share files, discuss details, and monitor progress in real-time.
            </FlowStepText>
          </FlowStep>

          <FlowStep>
            <StepNumber $bgColor={theme.colors.flow5.bg} $color={theme.colors.flow5.color} $shadow={theme.colors.flow5.shadow}>5</StepNumber>
            <FlowStepTitle>Pay When Done</FlowStepTitle>
            <FlowStepText>
              Review the completed work, mark the task as finished, and pay using your preferred payment method.
            </FlowStepText>
          </FlowStep>
        </FlowSteps>
      </UserFlowSection>
    </FeaturesSectionWrap>
  );
};

const FAQSection = () => {
  const items = [
    {
      q: "What is Doitly?",
      a: "Doitly is a marketplace where people post tasks and receive offers from taskers. You can compare proposals, chat, and mark tasks complete when done.",
    },
    {
      q: "How does Doitly work?",
      a: "Post your task with details and budget, review offers from taskers, collaborate via chat, and complete the task. You can arrange payment directly with your tasker.",
    },
    {
      q: "How do I pay for completed work?",
      a: "Payment is handled directly between you and the tasker after work completion. Popular payment methods include cash, Venmo, Zelle, PayPal, bank transfer, or any method you both agree upon. Doitly doesn't process payments, giving you flexibility to choose what works best.",
    },
    {
      q: "Why should I use Doitly instead of planning my task myself?",
      a: "Doitly gives you quick access to motivated taskers, better price discovery, faster responses, and a simple way to manage the entire process in one place.",
    },
    {
      q: "What if there's an issue during my task?",
      a: "Use chat to communicate clearly with the other party and attempt resolution. The platform doesn't mediate disputes, so agree on milestones and deliverables upfront.",
    },
  ];

  return (
    <FAQSectionWrap>
      <FAQHeading>Frequently Asked Questions</FAQHeading>
      <FAQSubheading>
        Discover how Doitly helps you post, manage, and complete tasks with
        confidence.
      </FAQSubheading>

      <FAQList>
        {items.map((it, i) => (
          <FAQItem key={it.q} id={i} q={it.q} a={it.a} />
        ))}
      </FAQList>
    </FAQSectionWrap>
  );
};

//================================================================//
//  COMPONENT
//================================================================//

const HowItWorks = ({ state }) => (
  <>
    <GlobalStyle />
    <PageWrapper>
      <NavBar user={state.user} />
      <ContentContainer>
        <Hero>
          <Title>
            Get Any Task Done.{" "}
            <span style={{ 
              background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Big or Small.</span>
          </Title>
          <Subtitle>
            Post what you need. Get multiple offers. Pick your favorite. 
            It's the easiest way to get help with anything.
          </Subtitle>
        </Hero>

        <ContentPane>
          <Grid>
            <Column>
              <SectionHeader>Post Your Task</SectionHeader>
              <Step>
                <IconCircle $bgColor={theme.colors.step1.bg} $color={theme.colors.step1.color}>
                  <FilePlus size={24} />
                </IconCircle>
                <StepContent>
                  <StepTitle>Describe What You Need</StepTitle>
                  <StepText>
                    Share the details of your task and set your budget. Clear descriptions get better offers from helpers.
                  </StepText>
                </StepContent>
              </Step>
              <Step>
                <IconCircle $bgColor={theme.colors.step2.bg} $color={theme.colors.step2.color}>
                  <Users size={24} />
                </IconCircle>
                <StepContent>
                  <StepTitle>Review Offers</StepTitle>
                  <StepText>
                    Skilled people will send offers with their pricing and approach. Compare options and choose who feels right.
                  </StepText>
                </StepContent>
              </Step>
              <Step>
                <IconCircle $bgColor={theme.colors.step3.bg} $color={theme.colors.step3.color}>
                  <MessageCircle size={24} />
                </IconCircle>
                <StepContent>
                  <StepTitle>Stay Connected</StepTitle>
                  <StepText>
                    Use built-in messaging to communicate, share files, and track progress throughout the project.
                  </StepText>
                </StepContent>
              </Step>
              <Step>
                <IconCircle $bgColor={theme.colors.step4.bg} $color={theme.colors.step4.color}>
                  <CheckCircle size={24} />
                </IconCircle>
                <StepContent>
                  <StepTitle>Complete and Pay</StepTitle>
                  <StepText>
                    Mark the task complete when you're satisfied. Pay using cash, Venmo, PayPal, or any method you both prefer.
                  </StepText>
                </StepContent>
              </Step>
            </Column>

            <Column>
              <SectionHeader>Earn Money Helping Others</SectionHeader>
              <Step>
                <IconCircle $bgColor={theme.colors.step1.bg} $color={theme.colors.step1.color}>
                  <Briefcase size={24} />
                </IconCircle>
                <StepContent>
                  <StepTitle>Browse Available Tasks</StepTitle>
                  <StepText>
                    Explore tasks that match your skills and interests. Filter by location, budget, and task type to find perfect opportunities.
                  </StepText>
                </StepContent>
              </Step>
              <Step>
                <IconCircle $bgColor={theme.colors.step2.bg} $color={theme.colors.step2.color}>
                  <Wand2 size={24} />
                </IconCircle>
                <StepContent>
                  <StepTitle>Submit Your Offer</StepTitle>
                  <StepText>
                    Send a personalized offer explaining your approach, timeline, and pricing. Show why you're the best choice for their task.
                  </StepText>
                </StepContent>
              </Step>
              <Step>
                <IconCircle $bgColor={theme.colors.step3.bg} $color={theme.colors.step3.color}>
                  <TrendingUp size={24} />
                </IconCircle>
                <StepContent>
                  <StepTitle>Deliver Excellence</StepTitle>
                  <StepText>
                    Once selected, communicate regularly and deliver quality work. Ask questions early and keep them updated on your progress.
                  </StepText>
                </StepContent>
              </Step>
              <Step>
                <IconCircle $bgColor={theme.colors.step4.bg} $color={theme.colors.step4.color}>
                  <DollarSign size={24} />
                </IconCircle>
                <StepContent>
                  <StepTitle>Get Paid</StepTitle>
                  <StepText>
                    Receive payment directly from the client using your preferred method. Build your reputation for future opportunities.
                  </StepText>
                </StepContent>
              </Step>
            </Column>
          </Grid>
        </ContentPane>

        {/* Platform Features Section */}
        <PlatformFeaturesSection user={state.user} />

        {/* FAQ Section */}
        <FAQSection />
      </ContentContainer>
    </PageWrapper>
  </>
);

const mapStateToProps = (state) => ({ state });
export default connect(mapStateToProps)(HowItWorks);
